import { Moment } from 'moment-timezone';
import { BankAccountSourceType } from '../enums/bank-account-source-type.enum';
import { TransactionImportSource } from '../enums/transaction-import-source.enum';

/**
 * SD-9119: the copy for the Financial Connections balance CTA, "Link account" for an account that
 * was never linked, "Reconnect" for one whose connection was severed at the bank.
 *
 * SD-8906 introduced the reconnect wording in the Transactions totals-box only. The v2 Treasury
 * Financial Accounts dashboard has its own, independent copy of the same CTA and kept saying
 * "Link account", so a single severed account read "Reconnect" on one screen and "Link account" on
 * the other, telling an admin to link an account they had already linked, on the very screen where
 * they manage those accounts.
 *
 * The two surfaces render deliberately different markup (an inline `.balance-link` anchor inside a
 * carousel card vs. a `linkButton` anchor in a `mat-list` meta slot) and are gated by different
 * permission mechanics, so they cannot collapse into one component. What must never diverge again is
 * the WORDS, so the words live here, and a third surface gets them for free instead of hardcoding a
 * fourth variant.
 *
 * SD-9845: sharing the words was NOT sufficient. The CONDITION stayed per-surface, so when SD-9584
 * changed the rule in core the Transactions card kept deciding from the raw `fcReconnectRequiredAt`
 * marker while the dashboard read core's reason-aware `isBalanceLinked` — and the two screens
 * disagreed by construction for any connection marked `inactive` inside the escalation window
 * (measured at 6 accounts across 3 orgs on 2026-09-04). The gate now lives on core's answer at both
 * surfaces; `bankAccountNeedsReconnect()` below is a LABEL/TOOLTIP selector ONLY and must never be
 * used to decide WHETHER to offer the CTA.
 *
 * Deliberately a plain function module rather than a service: this is pure copy selection with no
 * state and no injection, matching `transaction-display.ts` next door.
 */

/**
 * The only field these helpers read. Structurally satisfied by both `UnifiedBankAccount` (the
 * Transactions surface) and `ConnectedBankAccount` (the v2 dashboard surface), so neither has to
 * be imported here and the helper cannot pull a component's model graph into `functions/`.
 */
export interface ReconnectableBankAccount {
    fcReconnectRequiredAt: Moment | null;
}

/**
 * SD-10147 — the fields the money-capability helpers read, on top of the reconnect marker.
 *
 * Structurally satisfied by both `UnifiedBankAccount` (the Financial Accounts surface) and
 * `ConnectedBankAccount` (the v2 dashboard and Move Money surfaces), for the same reason the
 * interface above is: neither model is imported here, so this file cannot pull a component's model
 * graph into `functions/`.
 */
export interface MoneyCapableBankAccount extends ReconnectableBankAccount {
    id: number;
    isTransferEligible: boolean | null;
    isOutboundEligible: boolean | null;
}

/**
 * SD-10147 — CAN THIS ACCOUNT SEND MONEY RIGHT NOW? The single derived answer the ticket asks for,
 * stated once so all three surfaces give the same answer.
 *
 * It is deliberately a thin read of `isTransferEligible` rather than a calculation, because the
 * derivation belongs on the server and already happens there:
 * `UnifiedBankAccount::getIsTransferEligibleAttribute()` ANDs the payment capability with the
 * connection health (`connectionIsSevered()`), and appends the result to every payload. Re-deriving
 * it here from the raw columns would be a second opinion that can disagree with the guard the
 * server actually enforces — the exact divergence this ticket exists to remove. The value of this
 * function is that there is ONE place each surface asks the question, not that it computes anything.
 *
 * ⚠️ THERE IS NO SINGLE ANSWER FOR BOTH DIRECTIONS, AND THERE MUST NOT BE. The acceptance criteria
 * describe one `canMoveMoneyNow = capability AND connection-health`, but a severed connection stops
 * an account being DEBITED while leaving it perfectly payable: measured on production 2026-10-16,
 * 34 outbound transfers were attempted after the destination's marker was stamped and 33 posted
 * with zero failures. Folding both directions into one answer would withhold those payouts and
 * re-create the regression SD-9540 and SD-9569 each had to undo. So the one answer is split by
 * DIRECTION, not by surface: this for the source side, {@see accountCanReceiveMoney} for the
 * destination side. Every surface uses the same pair.
 */
export function accountCanSendMoney(account: MoneyCapableBankAccount): boolean {
    return !!account.isTransferEligible;
}

/**
 * SD-10147 — can this account be PAID, right now? The destination half of the pair above.
 *
 * Connection health is deliberately absent, and that absence is the measured decision recorded on
 * `UnifiedBankAccount::getIsOutboundEligibleAttribute()`. Do not add it here.
 */
export function accountCanReceiveMoney(account: MoneyCapableBankAccount): boolean {
    return !!account.isOutboundEligible;
}

/**
 * SD-10147 — does `other` still count as an account this organization can move money with, when
 * deciding whether `account` may be removed?
 *
 * The OR both delete guards use, and the server's own
 * `TreasuryBankAccountTeardownService::hasOtherConnectedAccount()`. Stated here so the Treasury
 * dashboard and the Financial Accounts page cannot drift from each other or from the server — they
 * previously held one copy each, which is three implementations of one question.
 *
 * It stays an OR on purpose: an account that can only RECEIVE is still a real counterparty, so a
 * severed connection still counts here. That is why the surfaces additionally warn when every
 * surviving account can only receive — the guard is right, and silence about the consequence is
 * what was wrong.
 */
export function accountCountsAsCapableOther(
    other: MoneyCapableBankAccount,
    account: MoneyCapableBankAccount,
): boolean {
    return other.id !== account.id && (accountCanSendMoney(other) || accountCanReceiveMoney(other));
}

/**
 * SD-8906: whether this account's Stripe connection was severed and cannot recover on its own.
 *
 * Keyed on `fcReconnectRequiredAt` alone rather than on the source type or the presence of an FC id:
 * the production victims are `financial_connections` accounts that DO still carry an `fca_`, a dead
 * one, so any id-presence test misses them. Core stamps this marker from its permanent-error
 * give-up handlers and clears it on a successful relink.
 *
 * ⚠️ SD-9845 — LABEL AND TOOLTIP SELECTION ONLY. Do NOT gate the CTA on this. The raw marker answers
 * "is this row's feed frozen?", which is NOT the same question as "must a human re-authorize right
 * now?" — since SD-9584 only core answers the second one, from the marker AND its reason
 * (`UnifiedBankAccount::reconnectCtaRequired()`), and it arrives as `isBalanceLinked`. Gating here
 * offered a Reconnect button on transient-inactive connections that Stripe reported ACTIVE and whose
 * balance refresh had SUCCEEDED, where the re-auth is at best pointless and at worst mints the rival
 * row that stranded org 46510's history (SD-9369, SD-9796). It stays keyed on the raw marker on
 * purpose: whenever a CTA IS shown, this is what distinguishes "Reconnect" from "Link account".
 */
export function bankAccountNeedsReconnect(account: ReconnectableBankAccount): boolean {
    return !!account.fcReconnectRequiredAt;
}

/** The fields the CTA decision reads, on top of the reconnect marker. */
export interface BalanceActionBankAccount extends ReconnectableBankAccount {
    sourceType: string | null;
    stripeFinancialConnectionsId: string | null;
    /** Core's explicit answer. `null` means core did not say, which is not the same as `false`. */
    isBalanceLinked: boolean | null;
}

/**
 * SD-8605: an external account added by routing/account number has no Financial Connections id, so
 * Treasury cannot pull its balance. Deliberately this narrow pair rather than "has no FC id":
 * legacy/Plaid rows have neither and must keep rendering their balance, and the link-balance
 * endpoint only accepts source_type=external_account.
 */
export function bankBalanceNeedsLink(account: BalanceActionBankAccount): boolean {
    return account.sourceType === BankAccountSourceType.EXTERNAL_ACCOUNT
        && ! account.stripeFinancialConnectionsId;
}

/**
 * SD-9845: whether to offer an action instead of a balance figure. Taken from core's
 * `isBalanceLinked` and never re-derived — see the warning on bankAccountNeedsReconnect above.
 *
 * The `??` fallback reproduces the pre-SD-9845 expression exactly, and only answers when core omits
 * the field, so a UI bundle running against an older core behaves as it did rather than reading
 * `undefined` as "not linked" and showing the CTA on every account.
 */
export function bankBalanceNeedsAction(account: BalanceActionBankAccount): boolean {
    return ! (account.isBalanceLinked
        ?? ! (bankBalanceNeedsLink(account) || bankAccountNeedsReconnect(account)));
}

/**
 * SD-9845: the feed is frozen but core is deliberately withholding the reconnect CTA, because the
 * freeze is a transient `inactive` still inside the escalation window. Cannot be expressed by
 * either signal alone, which is why the balance used to render as though it were current.
 */
export function bankBalanceUpdatesPaused(account: BalanceActionBankAccount): boolean {
    return bankAccountNeedsReconnect(account) && ! bankBalanceNeedsAction(account);
}

/**
 * The only field the balance-capability helpers read, kept separate from `ReconnectableBankAccount`
 * so a caller that has only one of the two fields still type-checks.
 */
export interface BalanceCapableBankAccount {
    sourceType: string | null;
    /**
     * SD-9792: the one `manual` row a balance can arrive for. Required, not optional — an optional
     * flag reads as false wherever a caller forgets to map it, which is the silent "Not available"
     * this ticket fixes.
     */
    isAllianceAccount: boolean;
}

/**
 * SD-8671 review follow-up (F-73c4): whether a bank balance can NEVER be obtained for this account,
 * as opposed to simply not having arrived yet.
 *
 * This is a third state the balance UI did not previously have, and admitting `manual` accounts to
 * the Financial Accounts card is what created the need for it. Every other account in that list can
 * in principle produce a balance: a `financial_connections` row has a live link, and an
 * `external_account` row can be given one through the link-balance CTA. A `manual` row can do
 * neither — it was created from typed digits, has no Financial Connections link, and the
 * link-balance endpoint accepts only `external_account`
 * (`LinkExternalAccountBalanceRequest::withValidator`), so offering the CTA surfaces a button the API
 * refuses. Without this distinction the card had only two wrong options for such a row: the rejected
 * CTA, or a `--` placeholder that never resolves and reads as "still loading" forever.
 *
 * Deliberately keyed on the source type rather than on "has no FC id": an `external_account` row also
 * has no FC id, and for that one the CTA is exactly right.
 *
 * SD-9792 — WESTERN ALLIANCE IS THE EXCEPTION, and the paragraph above overstated its case. "A
 * `manual` row can do neither" holds only for rows whose balance must come through a bank
 * CONNECTION; a WAB account reports through Alliance's own API, which core already pulls into
 * `plaid_balance` daily. So this predicate was asserting "never" over a balance the system actively
 * fetches, and it wins ahead of the balance fork. Escalated on org 47170, account ····3621.
 */
export function bankBalanceIsUnobtainable(account: BalanceCapableBankAccount): boolean {
    return account.sourceType === BankAccountSourceType.MANUAL && ! account.isAllianceAccount;
}

/**
 * What to show in place of a balance figure when none can ever arrive. Stated positively rather than
 * as a dash, so an admin can tell "this account does not report a balance" from "we are waiting".
 */
export const BANK_BALANCE_UNOBTAINABLE_LABEL = 'Not available';

/**
 * The only fields the balance-confidence helper reads, kept separate from the interfaces above for
 * the same reason: a caller holding only these two still type-checks.
 */
export interface BalanceConfidenceBankAccount {
    /**
     * Appended by core's `UnifiedBankAccount::appendRelatedFields()`. Nullable so "core did not say"
     * stays distinguishable from a real `false`, see the helper below, where that difference decides
     * whether an older core suppresses every balance or none.
     */
    isBalanceConfirmed: boolean | null;
}

/**
 * SD-9632 / SD-9273: is the balance we hold a figure the BANK actually reported?
 *
 * A fourth balance state, and the one the UI had no way to express. Core stores a literal `0` both
 * for a genuinely empty account and for one whose balance Stripe had not computed yet, so the old
 * fork, `(fcBalance ?? plaidBalance) !== null`, sent an unretrieved balance straight into the
 * currency branch and rendered a confident `$0.00`. That reads as a fact: 12 live orgs displayed one,
 * including a RESERVE account, where a treasurer reconciling against a bank statement had no signal
 * anything was missing. The sibling case is SD-9273's bare dash, which was honest but unexplained.
 *
 * Deliberately NOT computed here from `fcBalance === 0`. Whether a zero is real depends on Stripe's
 * `balance.as_of`, which only core holds, and re-deriving the rule client-side would guarantee the
 * two drift apart. This reads core's answer and nothing else.
 *
 * Returns FALSE when the flag is absent, which is the load-bearing default. A UI bundle running
 * against a core that predates the append gets `null`, and treating that as unconfirmed would hide
 * EVERY bank balance in the product rather than the handful that are actually unproven. Fail toward
 * showing the figure; the figure was already what shipped before this ticket.
 */
export function bankBalanceIsUnconfirmed(account: BalanceConfidenceBankAccount): boolean {
    return account.isBalanceConfirmed === false;
}

/**
 * SD-11249: the fields that decide WHICH connection's balance an account shows.
 *
 * A structural shape rather than the entity, deliberately: `UnifiedBankAccount` uses this for its
 * getters, and the connected-accounts suite passes bare object literals (review F-8cd4 documents
 * that it stubs BankAccountsService so no entity is ever built). One definition has to serve both,
 * or the rule gets copied and the copies drift.
 *
 * Every field is OPTIONAL, which is what makes this a no-op for anything that does not supply them:
 * an absent `transactionImportSource` reads `undefined`, is not PLAID, and the FC figure wins
 * exactly as it always did.
 */
export interface ImportPreferenceBankAccount {
    transactionImportSource?: string | null;
    /**
     * BOTH, deliberately. Core's rule is `plaid_token_id !== null` — a plain column that every
     * payload carries — whereas `plaidToken` is a RELATION that is only present when the endpoint
     * eager-loaded it (`getOrgBankAccounts` asks only for `fixedAsset`). Keying on the relation
     * alone would make this silently answer "no Plaid link" on exactly the screens the fix is for,
     * and the fix would quietly do nothing.
     */
    plaidTokenId?: number | null;
    plaidToken?: unknown;
    stripeFinancialConnectionsId?: string | null;
}

/**
 * SD-10930/SD-11249: do the automatic imports read `source` for this account?
 *
 * Mirrors core's `UnifiedBankAccount::importsTransactionsFrom()`: no recorded preference reads
 * every connection, and a preference silences the other connection ONLY while the preferred link
 * actually exists — so a preference for a link the row does not hold silences nothing, and no
 * account is ever left importing from, or showing a balance from, nothing at all.
 *
 * A FUNCTION over a structural shape rather than a method on the entity, deliberately: the
 * financial-accounts and connected-accounts suites both pass bare object literals, so an entity
 * method is simply absent there (`acct.importsTransactionsFrom is not a function`). One definition
 * that serves entities and literals alike is the only version that cannot break one of them.
 */
export function accountImportsTransactionsFrom(
    account: ImportPreferenceBankAccount,
    source: TransactionImportSource,
): boolean {
    const preferred = account?.transactionImportSource;
    if (!preferred || preferred === source) {
        return true;
    }
    return !accountHoldsImportLink(account, preferred);
}

/**
 * Whether the row holds the link a given import source reads from. Accepts EITHER `plaidTokenId`
 * (the column core's own rule uses, always present in a payload) or the `plaidToken` relation,
 * which only some endpoints eager-load.
 */
export function accountHoldsImportLink(account: ImportPreferenceBankAccount, source: string): boolean {
    return source === TransactionImportSource.PLAID
            ? (account?.plaidTokenId != null || !!account?.plaidToken)
            : !!account?.stripeFinancialConnectionsId;
}

/**
 * SD-11249: does this account show its PLAID balance rather than its Financial Connections one?
 * Defined in terms of the predicate above so there is exactly one rule.
 */
export function accountRendersPlaidBalance(account: ImportPreferenceBankAccount): boolean {
    return !accountImportsTransactionsFrom(account, TransactionImportSource.FINANCIAL_CONNECTIONS);
}

/**
 * SD-11249: the balance to show — the one belonging to the connection the account actually imports
 * from, not whichever column happens to be populated.
 *
 * Converting an association changes where its money LANDS, not how its bank is CONNECTED. The bank
 * that becomes the Treasury payout destination acquires a Financial Connections link but keeps
 * importing through Plaid, so `fcBalance ?? plaidBalance` would put a Stripe figure in front of the
 * Plaid balance the admin reconciles against.
 */
export function renderedBankBalanceCents(
    account: ImportPreferenceBankAccount & { fcBalance?: number | null; plaidBalance?: number | null },
): number | null {
    // Review F-89db: an account that renders the Plaid figure does NOT fall back to the Financial
    // Connections one. The earlier `plaidBalance ?? fcBalance ?? null` put the Stripe figure on
    // screen whenever Plaid had not reported yet — while renderedBankBalanceIsUnconfirmed() was
    // simultaneously returning false for that same account on the premise that the FC figure was
    // "held but not shown". The two together rendered an unproven 0 as a confident $0.00, which is
    // precisely the fabricated zero SD-9632 exists to prevent, and which WAS suppressed before this
    // change. Showing nothing is correct here: with no Plaid figure yet the caller's own null check
    // renders "Balance pending", exactly as it did before.
    if (accountRendersPlaidBalance(account)) {
        return account.plaidBalance ?? null;
    }

    return account.fcBalance ?? account.plaidBalance ?? null;
}

/**
 * The fields needed to know WHICH figure is about to be rendered, not just whether the FC one is
 * trustworthy.
 */
export interface RenderedBalanceBankAccount extends BalanceConfidenceBankAccount, ImportPreferenceBankAccount {
    fcBalance: number | null;
}

/**
 * Is the figure a card is ABOUT TO RENDER one we cannot vouch for? (review F-40d2)
 *
 * The Transactions cards render the balance of the connection the account actually imports from, so
 * the value on screen is not always the Financial Connections figure, and `isBalanceConfirmed` only
 * ever describes that one. Applying it to the rendered value means an account whose balance
 * legitimately arrives via Plaid could have a perfectly real figure replaced by "Balance pending".
 *
 * SD-11249 made that reachable and so closed it properly. The cards no longer coalesce
 * `fcBalance ?? plaidBalance`; a converted association's payout-destination bank holds an FC link it
 * does NOT import from, so it can carry an unconfirmed `fcBalance` while rendering its Plaid
 * balance. `rendersPlaidBalance` is the account's own answer to "which figure is on screen", taken
 * from the one preference rule rather than re-derived here.
 *
 * (When review F-40d2 added this, no production row was in that state — checked 2026-09-04: zero
 * FC-backed rows held a null `fc_balance` beside a non-null `plaid_balance`.)
 *
 * A null `fcBalance` returns false rather than true: there is no FC figure to distrust, so the
 * decision passes to the caller's own null check, which is what still renders "Balance pending" for
 * a newly linked account with no balance at all (SD-9273).
 */
export function renderedBankBalanceIsUnconfirmed(account: RenderedBalanceBankAccount): boolean {
    if (account.fcBalance === null || account.fcBalance === undefined) {
        return false;
    }

    // SD-11249: the FC figure is held but not shown, so its confidence says nothing about the
    // number on screen.
    if (accountRendersPlaidBalance(account)) {
        return false;
    }

    return bankBalanceIsUnconfirmed(account);
}

/**
 * What to show instead of a figure we cannot back.
 *
 * Says "pending" rather than a dash or `$0.00` because both of those assert something untrue: the
 * dash reads as "still loading, forever" (the entire SD-9273 customer complaint) and `$0.00` reads as
 * a reconciled fact. "Pending" is the honest third answer, we are waiting on the bank.
 */
export const BANK_BALANCE_PENDING_LABEL = 'Balance pending';

/**
 * Why the balance is pending, in an admin's terms rather than ours.
 *
 * Deliberately says nothing about Stripe, `as_of`, or refresh windows: the actionable content is
 * that the figure is not ours to report yet and that nothing is broken. It explicitly rules out zero,
 * because the whole failure this replaces was an admin reading an unretrieved balance as "empty".
 */
export const BANK_BALANCE_PENDING_TOOLTIP =
    'Your bank has not reported a balance for this account yet, so there is nothing to show. '
    + 'This is not a balance of $0.00. Balances refresh daily, and transactions are unaffected.';

/**
 * SD-9932: the source types Treasury actually reports a balance for.
 *
 * Mirrors core's `UnifiedBankAccount::FC_BACKED_SOURCE_TYPES` (UnifiedBankAccount.php:47), which is
 * exactly these two. Keep the two lists in step: core decides whether a balance can be FETCHED from
 * these rows, and this decides whether we are allowed to say we are WAITING on one.
 */
const FC_BACKED_SOURCE_TYPES: readonly string[] = [
    BankAccountSourceType.FINANCIAL_CONNECTIONS,
    BankAccountSourceType.EXTERNAL_ACCOUNT,
];

/**
 * The fields the pending-wording gate reads.
 *
 * `isTreasuryAccount` is here rather than assumed, and it is not decoration: see the gate below.
 */
export interface PendingWordingBankAccount {
    sourceType: string | null;
    isTreasuryAccount?: boolean;
    /**
     * SD-9932 (review F-2056): Western Alliance reports this account's balance through Alliance's
     * OWN API, which core pulls into `plaid_balance` daily, so a balance really is on its way even
     * though the row is not FC-backed and the org may have no Treasury. Withholding the wording
     * from it re-creates the "stuck loading forever" complaint the wording was introduced to fix.
     */
    isAllianceAccount?: boolean;
}

/**
 * What the balance slot said before SD-9632, and what it says again for every account outside
 * Treasury's reach.
 *
 * A dash asserts nothing, which is the point. It is not "Not available" (that claims we have
 * established a balance can never arrive) and not "Balance pending" (that promises one is coming).
 */
export const BANK_BALANCE_UNKNOWN_DASH = '--';

/**
 * SD-9932 (review F-6e2e): the v2 Transactions card's OWN prior string, which is NOT the dash.
 *
 * The two surfaces did not say the same thing before SD-9632 — v1 showed `--` and v2 showed `N/A`
 * (verified against `3847e6cc5^`) — and SD-9632 accidentally unified them by rewording both to
 * "Balance pending". Restoring "exactly what it showed before" therefore means a DIFFERENT string
 * per surface, so the fallback is a required argument below rather than a default: each call site
 * has to state its own history instead of inheriting the other surface's.
 */
export const BANK_BALANCE_UNKNOWN_NA = 'N/A';

/**
 * SD-9932: is "Balance pending" this account's answer AT ALL?
 *
 * "Balance pending" is a TREASURY concept and was only ever meant for Treasury accounts on
 * Treasury-enabled orgs. SD-9632 (#3503) did not introduce it that narrowly: it reworded the
 * GENERIC empty-balance fallback in the balance slot, the `else` of "do we have a figure to show",
 * which every account with no figure reaches whether the org has Treasury or not. A neutral `--`
 * therefore became a promise across the whole product, measured at 16,302 accounts in 7,243
 * non-Treasury orgs against 305 accounts in the 210 orgs it was written for.
 *
 * ⚠️ The wording is gated here rather than in `bankBalanceIsUnobtainable()` because the label is
 * NOT produced by a predicate. `renderedBankBalanceIsUnconfirmed()` returns FALSE when `fcBalance`
 * is null, so a row with no balance at all never reaches the confidence branch — it falls through
 * to the fallback, and it is the fallback's WORDS that changed. Gating a predicate instead means
 * re-litigating every account shape against that branch forever; gating the words is one rule.
 *
 * Both conditions are load-bearing. The org flag alone would still promise a balance for a typed-in
 * row inside a Treasury org, and the source type alone would still promise one for an org with no
 * pipeline to deliver it.
 *
 * ⚠️ "Typed-in" and not "manual" (review F-2056 corrected this sentence). A `manual` row flagged
 * `isAllianceAccount` DOES have a pipeline — SD-9792 established that in this very file — so it is
 * admitted below and is not an example of an over-promise.
 *
 * This is a SCOPING ROLLBACK, not a revert: the 210 Treasury orgs keep SD-9632's improvement, which
 * is the whole reason the wording exists.
 */
export function bankBalancePendingApplies(
    account: PendingWordingBankAccount,
    orgHasTreasuryEnabled: boolean,
): boolean {
    // SD-9932 (review F-2056): a Western Alliance row, and it is checked BEFORE the org gate ON
    // PURPOSE. Alliance is not a Treasury pipeline and Alliance orgs are typically NOT
    // Treasury-enabled (org 25821 carries the Alliance credential and has no Treasury flag), so
    // placing this after the gate below made it dead code for the entire cohort it exists for —
    // caught by its own tests before this shipped.
    //
    // SD-9792 already established in this file that Alliance reports these balances through its own
    // API, which core pulls into `plaid_balance` daily; that is why `bankBalanceIsUnobtainable()`
    // exempts them and why they land in this slot with a null balance until the first pull. On the
    // replica 1,252 rows across 643 orgs ALREADY hold an Alliance-fed balance, which is the proof
    // the feed works, against 375 rows across 274 orgs still awaiting their first. For those 375 a
    // balance genuinely is coming, so "pending" is the honest word and the fallback would be the
    // "reads as still loading, forever" defect this wording replaced.
    //
    // Not a hole in the Treasury scope: the scope exists to stop us PROMISING a balance that no
    // pipeline will deliver. Alliance is a pipeline that demonstrably delivers.
    if (account.isAllianceAccount === true) {
        return true;
    }

    if (! orgHasTreasuryEnabled) {
        return false;
    }

    // ⚠️ THE TREASURY BALANCE ROW IS A TREASURY ACCOUNT, and `FC_BACKED_SOURCE_TYPES` does not
    // contain it. That list answers "can Financial Connections reason about this row's balance",
    // which is a narrower question than "is this Treasury's own account": core gives the PayHOA
    // Balance row `source_type = 'treasury'` and keeps its cash in the Stripe financial account
    // rather than behind an FC connection.
    //
    // Keyed on `isTreasuryAccount` rather than a fourth source-type string because that is the flag
    // the UI model already carries for exactly this row, and the local `BankAccountSourceType` enum
    // has no `treasury` member to compare against.
    //
    // Caught by pre-computing the rendered labels before recording: without this the PayHOA Balance
    // card dropped to "--" on a Treasury org, which is 203 accounts across 203 orgs on the replica
    // and the most visible Treasury surface in the product. It is the exact population this wording
    // was written for, so excluding it would have inverted the ticket.
    if (account.isTreasuryAccount === true) {
        return true;
    }

    return account.sourceType !== null
            && account.sourceType !== undefined
            && FC_BACKED_SOURCE_TYPES.includes(account.sourceType);
}

/**
 * The word in the balance slot when there is no figure: "Balance pending" for a Treasury account on
 * a Treasury org, otherwise the pre-SD-9632 dash.
 *
 * Lives in this module for the same reason the CTA copy does (SD-9119): both shared Transactions
 * surfaces render this slot, and the two saying it differently is the class of defect this file
 * exists to prevent.
 */
export function bankBalancePendingLabel(
    account: PendingWordingBankAccount,
    orgHasTreasuryEnabled: boolean,
    fallbackLabel: string,
): string {
    return bankBalancePendingApplies(account, orgHasTreasuryEnabled)
            ? BANK_BALANCE_PENDING_LABEL
            : fallbackLabel;
}

/**
 * The tooltip, gated with the label and never separately.
 *
 * This half matters more than the label. It states that balances refresh daily, which is a promise
 * about a pipeline a non-Treasury org does not have, so shipping the dash while leaving the tooltip
 * attached would keep the misleading sentence and merely hide the word in front of it.
 *
 * Returns an empty string outside Treasury, which leaves `matTooltip` inert rather than rendering an
 * empty bubble — the same convention `bankBalanceActionTooltip()` above already uses.
 */
export function bankBalancePendingTooltip(
    account: PendingWordingBankAccount,
    orgHasTreasuryEnabled: boolean,
): string {
    return bankBalancePendingApplies(account, orgHasTreasuryEnabled)
            ? BANK_BALANCE_PENDING_TOOLTIP
            : '';
}

/**
 * The only field the microdeposit-hold helper reads, kept separate from the two interfaces above so
 * a caller that has only one of the three fields still type-checks. Structurally satisfied by both
 * `UnifiedBankAccount` and `ConnectedBankAccount`.
 */
export interface MicrodepositHeldBankAccount {
    microdepositVerificationRequiredAt: Moment | null;
    /**
     * SD-9613: which DIRECTIONS actually work, which the hold above can no longer answer on its own.
     *
     * Optional for a reason worth stating exactly, because the first version of this comment got it
     * wrong: it is NOT that callers predate the flag — both caller types declare
     * `isTransferEligible`. It is that `UnifiedBankAccount` coerces it with `?? null` on construction
     * (`unified-bank-account.ts`), so a payload that omits the field yields null rather than a
     * boolean, and null is neither true nor false to the consumer.
     *
     * That path is not expected in practice — core's `UnifiedBankAccountController::get()` calls
     * `appendRelatedFields()`, which appends the flag — so this is belt-and-braces. It still has to
     * resolve somewhere, and it resolves to the NARROWER answer: an account we cannot vouch for must
     * not be told transfers still work.
     */
    isTransferEligible?: boolean;
}

/**
 * SD-9078 stamps this while Stripe waits for the two small deposits to be confirmed, and both
 * transfer guards refuse the account until it clears.
 */
export function bankAccountAwaitsMicrodeposits(account: MicrodepositHeldBankAccount): boolean {
    return !!account.microdepositVerificationRequiredAt;
}

/**
 * Why no balance will arrive. Always true for a manual row: the account was created from typed
 * digits, so there is no bank connection to report through.
 */
export const BANK_BALANCE_NOT_SHARED_SENTENCE =
        'Accounts added by routing and account number don\'t share balances with PayHOA.';

/**
 * The reassurance that the account still works for transfers — which is the question an admin
 * actually has when they see the label above on an account they just added.
 *
 * SD-9557: kept SEPARATE from the sentence above because it is only sometimes true, and appending it
 * unconditionally is what made the tooltip false. See `bankBalanceUnobtainableTooltip()`.
 */
export const BANK_TRANSFERS_STILL_WORK_SENTENCE = 'Transfers still work.';

/**
 * SD-9613 (F-a1be). The per-direction answer for a receive-only account: payable, not debitable.
 *
 * The sentence above became false for the whole cohort it is shown to. Core stopped attaching the
 * inbound direction on a manual add, so the account can be paid out to and can never be debited —
 * and "Transfers still work." reads as covering both. This is the same correction already made in the
 * add-account dialog and the success growler; the tooltip renders on two more screens and was missed.
 */
export const BANK_RECEIVE_ONLY_SENTENCE = 'You can send payouts to it, but not send money from it.';

/**
 * SD-9557: the tooltip, composed per account rather than held as one constant.
 *
 * The transfer-capability sentence describes the intended design and is true for a manual row whose
 * SetupIntent succeeded — but a row still awaiting microdeposit verification cannot transfer in
 * EITHER direction, and per SD-9564 nothing in our code observes that confirmation, so the hold is
 * never cleared. Every manual row in production WAS held when that was written, which made a sentence
 * reading as reassurance false for 100% of the accounts that actually saw it. SD-9613 ended that —
 * see the correction below, which is why this paragraph is past tense.
 *
 * The suppression is silence, not a substitute instruction, and that is deliberate: a held manual row
 * has no self-service remedy to offer. `LinkExternalAccountBalanceRequest::withValidator` admits only
 * `external_account` rows, or a `financial_connections` row with `fc_reconnect_required_at` set, and
 * explicitly refuses `manual` — so naming an action here would be a second false promise. The balance
 * clause and the "Not available" label above it are both still correct and are left in place.
 *
 * SD-9613 (F-a1be) adds the middle case, which the two branches above could not express. The hold is
 * no longer set on a manual add, so a brand-new account falls to the final branch and was told
 * "Transfers still work." — true of the payout direction and false of the other. The premise the
 * docblock above rested on ("every manual row in production is held") stopped holding the moment
 * core#5356 shipped, which is why that paragraph now reads in the past tense; three sentences now,
 * one per real state:
 *
 *   - HELD — deposits outstanding, nothing works, and nothing to instruct. Unchanged.
 *   - RECEIVE-ONLY — payable, not debitable. The new middle branch.
 *   - FULLY USABLE — a legacy manual account that completed verification. Keeps its reassurance,
 *     which is why this cannot simply be swapped for the receive-only wording.
 *
 * `isTransferEligible` is optional on the input shape, and a missing value reads as NOT eligible: the
 * receive-only sentence is the safe answer to "we cannot tell", where the reassurance is not.
 */
export function bankBalanceUnobtainableTooltip(account: MicrodepositHeldBankAccount): string {
    if (bankAccountAwaitsMicrodeposits(account)) {
        return BANK_BALANCE_NOT_SHARED_SENTENCE;
    }

    if (account.isTransferEligible !== true) {
        return `${BANK_BALANCE_NOT_SHARED_SENTENCE} ${BANK_RECEIVE_ONLY_SENTENCE}`;
    }

    return `${BANK_BALANCE_NOT_SHARED_SENTENCE} ${BANK_TRANSFERS_STILL_WORK_SENTENCE}`;
}

/**
 * The only field the paused-updates helpers read, kept separate from the interfaces above so a
 * caller that has only one of the fields still type-checks. Structurally satisfied by both
 * `UnifiedBankAccount` (the Transactions surface) and `ConnectedBankAccount` (the v2 dashboard).
 */
export interface StaleBalanceBankAccount {
    fcBalanceRefreshedAt: Moment | null;
}

/**
 * SD-9845: what the SUPPRESSED state says.
 *
 * SD-9584 withholds the reconnect CTA for a transient `inactive` connection until the daily reprobe
 * has had its window, which is right — 41 of 50 recorded recoveries were a person clicking through
 * a prompt the reprobe would have cleared. But withholding the CTA left both surfaces rendering a
 * confident bank balance stamped BEFORE the feed died, with nothing to say the feed was paused:
 * org 46510's ····2047 showed $21,911.76 from 2026-09-03 05:09 UTC as though it were current. That
 * is the SD-9347 silent-dead-feed shape, and it is why the SD-9845 gate fix could not simply
 * suppress the CTA on both screens and stop there.
 *
 * States WHEN THE BANK LAST GAVE US DATA, and nothing else. Deliberately not "updates are paused"
 * (the first cut) and not "temporarily unavailable": both characterise the outage — one implying
 * someone chose to pause it and a resume exists, the other implying we know it is temporary — and we
 * know neither. What we can state as fact is the timestamp of the last figure the bank actually
 * served, which is also the only thing the admin needs in order to judge the number beside it.
 *
 * Worded to MATCH THE OTHER FEED rather than to be novel: a Plaid account already carries a
 * "Last pulled <date>" tooltip in this exact slot on the Transactions card
 * (`totals-box.component.html`), so an admin meets one sentence for "here is how old this figure
 * is", whichever feed the account is on. Copy decided by Stephen, 2026-09-04.
 *
 * BYTE-IDENTICAL to that Plaid tooltip on purpose — no "at", no trailing full stop — so the two
 * feeds cannot read as two different notices sitting inches apart on the same card. If you are
 * tempted to punctuate this into a sentence, change the Plaid one in the same commit or leave both
 * alone.
 *
 * Offers no action, because while the CTA is suppressed there is none, and naming one would be the
 * false promise `bankBalanceUnobtainableTooltip()` above already avoids.
 *
 * NOTE the quantity differs from the Plaid tooltip it mirrors, even though the wording matches:
 * Plaid's reads `plaidToken.transactionsLastPulled` (when TRANSACTIONS last came in), this one reads
 * `fc_balance_refreshed_at` (when the BALANCE was last refreshed). Both answer "how stale is what I
 * am looking at", and this tooltip sits on the balance, so it is the right field here — but do not
 * "unify" them onto one column on the strength of the shared wording.
 */
export const BANK_BALANCE_LAST_PULLED_PREFIX = 'Last pulled';

/**
 * The same disclosure for a row the bank has never served a balance for at all — an FC connection
 * that broke before its first successful refresh. `fc_balance_refreshed_at` is null there, and
 * "Last pulled " with nothing after it reads as a bug.
 *
 * Unpunctuated to match its sibling above rather than to read as a sentence on its own.
 */
export const BANK_BALANCE_NEVER_PULLED_SENTENCE = 'Not yet pulled from bank';

/**
 * The format the neighbouring Plaid "Last pulled" tooltip renders through `legfiDate`.
 *
 * Duplicated here as a literal rather than piped in the template BECAUSE the whole sentence has to
 * live in this module: SD-9119 exists because the same CTA was worded twice, once per surface, and
 * composing "paused" + "last updated" in each template would rebuild that exact drift. For a
 * `Moment` value `LegfiDatePipe` delegates to `value.format(pattern)` verbatim
 * (`legfi-date.pipe.ts` → `formatWithMoment()`), so this renders identically to the pipe.
 */
export const BANK_BALANCE_UPDATED_AT_FORMAT = 'MM/DD/YYYY h:mm a';

/**
 * The tooltip for the suppressed state, composed per account.
 *
 * Two forms rather than one with a dropped clause: the whole sentence is ABOUT the timestamp, so
 * with no timestamp there is no sentence to trim — it needs a different one. Same shape as
 * `bankBalanceUnobtainableTooltip()` above, which also varies rather than truncating.
 *
 * Named for the STATE it belongs to (`balanceUpdatesPaused()` on both surfaces gates it), not for
 * the words it returns — the copy deliberately does not characterise the outage, but the internal
 * condition genuinely is "the feed is frozen and core is withholding the CTA".
 */
export function bankBalanceUpdatesPausedTooltip(account: StaleBalanceBankAccount): string {
    if (!account.fcBalanceRefreshedAt) {
        return BANK_BALANCE_NEVER_PULLED_SENTENCE;
    }

    return `${BANK_BALANCE_LAST_PULLED_PREFIX} `
            + `${account.fcBalanceRefreshedAt.format(BANK_BALANCE_UPDATED_AT_FORMAT)}`;
}

/**
 * The CTA label. A reconnect and a first-time link are the same request but not the same user story,
 * so the label has to differ.
 *
 * `inFlight` is passed in rather than read off the account on purpose: both surfaces hold the
 * in-flight account id on the COMPONENT (`linkingAccountId`), because their account objects are
 * rebuilt from scratch by a balance poll and any transient flag stored on them is wiped mid-journey.
 */
export function bankBalanceActionLabel(account: ReconnectableBankAccount, inFlight: boolean): string {
    if (inFlight) {
        return bankAccountNeedsReconnect(account) ? 'Reconnecting...' : 'Linking...';
    }

    return bankAccountNeedsReconnect(account) ? 'Reconnect' : 'Link account';
}

/**
 * The CTA tooltip. Empty for the first-time-link case, which needs no explanation, the account has
 * simply never been linked, and an empty string leaves `matTooltip` inert rather than rendering a
 * blank bubble.
 */
export function bankBalanceActionTooltip(account: ReconnectableBankAccount): string {
    return bankAccountNeedsReconnect(account)
            ? 'This account was disconnected at your bank, so its balance and transactions have stopped updating. Reconnect to resume them.'
            : '';
}

/**
 * The only field the terminal-connection helpers read, kept separate from the interfaces above for
 * the same reason they are separate from each other: a caller holding only this one still
 * type-checks. Structurally satisfied by both `UnifiedBankAccount` and `ConnectedBankAccount`.
 */
export interface TerminalConnectionBankAccount {
    isConnectionTerminal: boolean | null;
}

/**
 * SD-10133: whether re-linking THIS connection can never work, so a Reconnect CTA would be a lie.
 *
 * Core's verdict, not a re-derivation. The reason lives in `fc_reconnect_reason` — deleted at the
 * bank, created without a permission that cannot be added later, or severed — and this client never
 * receives it. Note this is a strictly narrower question than `bankAccountNeedsReconnect()` above:
 * every terminal row is frozen, but almost no frozen row is terminal (5 of 71 gated rows on
 * 2026-09-22), which is why it cannot be inferred from the marker.
 *
 * `=== true` deliberately, not truthiness: the field is null when core did not say, and null must
 * read as NOT terminal so a bundle running against a core that predates the append keeps exactly
 * today's behaviour. Failing the other way tells a customer with a working account it is dead.
 *
 * ⚠️ Unlike `bankAccountNeedsReconnect()`, this one IS a gate, and must be applied at every surface
 * that offers the CTA. SD-9119 and SD-9845 are both the same story — a per-surface answer to a
 * shared question — and a terminal row reads `isBalanceLinked: false` exactly like a recoverable
 * one, by design (core deliberately leaves `reconnectCtaRequired()` alone so the card cannot render
 * a dead account's stale balance as healthy). So `bankBalanceNeedsAction()` cannot distinguish them
 * and a surface that consults only that one keeps offering the dead button.
 */
export function bankConnectionTerminal(account: TerminalConnectionBankAccount): boolean {
    return account.isConnectionTerminal === true;
}

/**
 * What the balance slot says instead of a figure or a CTA once the connection is terminal.
 *
 * Says the connection ENDED rather than that something failed, because nothing here is retryable —
 * the bank has to be added again from scratch.
 */
export const BANK_CONNECTION_TERMINAL_LABEL = 'Connection ended';

/** Always true, and the reason the CTA is gone — stated before anything is asked of the reader. */
export const BANK_CONNECTION_ENDED_SENTENCE =
        'This bank connection can no longer be restored, so reconnecting it will not work.';

/** Named after the control itself, so the instruction points at something visible on the page. */
export const BANK_CONNECTION_ADD_ACCOUNT_SENTENCE = 'Use Add account to link this bank again.';

/** The reassurance that makes the state safe to leave alone; see `bankConnectionTerminalTooltip()`. */
export const BANK_CONNECTION_TRANSACTIONS_STAY_SENTENCE =
        'Your existing transactions stay exactly as they are.';

/**
 * The matching explanation for {@see BANK_CONNECTION_TERMINAL_LABEL}, composed per VIEWER rather
 * than held as one constant.
 *
 * SD-10133 review (F-b128): the Add account button is gated on write access to financial accounts,
 * but the "Connection ended" label is not and must not be — hiding it would put a read-only member
 * back in front of a frozen balance with no explanation, which is the SD-9347 silent-dead-feed
 * failure this ticket exists to end. So the LABEL is for everyone and only the INSTRUCTION is
 * withheld, following the precedent set by `bankBalanceUnobtainableTooltip()` above: where there is
 * no action the reader can take, the suppression is silence rather than a substitute instruction.
 *
 * The other two sentences are unconditional because both stay true for a read-only member: the
 * connection really is gone, and their transaction history really is untouched. What they lose is
 * only the sentence naming a button they cannot see.
 */
export function bankConnectionTerminalTooltip(canAddAccount: boolean): string {
    return canAddAccount
            ? `${BANK_CONNECTION_ENDED_SENTENCE} ${BANK_CONNECTION_ADD_ACCOUNT_SENTENCE} ${BANK_CONNECTION_TRANSACTIONS_STAY_SENTENCE}`
            : `${BANK_CONNECTION_ENDED_SENTENCE} ${BANK_CONNECTION_TRANSACTIONS_STAY_SENTENCE}`;
}
